import { SuccessData } from 'types/api';
import { executeApiCall } from 'services/api';
import { getMutations } from 'entities/hooks/mutation';
import { FullProjectTask } from '../types';

type ChangeOwnershipParams = {
  project: string;
  justification: string;
  task: string;
} & ({ owner: string; subOwner?: never } | { owner?: never; subOwner: string | null });

export const taskMutations = getMutations({
  assignTask: {
    action: async (payload: {
      task: string;
      project: string;
      owner: string;
    }): Promise<FullProjectTask> => {
      return executeApiCall({
        path: '/projects/tasks/assign',

        payload,
      });
    },

    messages: {
      success: (t) => t('task-assigned-with-success')!,

      error: (t) => t('error-while-assigning-task')!,
    },
  },

  changeTaskStatus: {
    action: async (payload: {
      task: string;
      project: string;
      status: string;
      justification?: string;
    }): Promise<SuccessData> => {
      return executeApiCall({
        path: '/projects/tasks/status',

        payload,
      });
    },

    messages: {
      error: (t) => ({
        title: t('could-not-change-task-status'),
        description: t('try-again-in-a-few-moments') as string,
        type: 'error',
      }),
    },
  },

  changeTaskName: {
    action: async (payload: {
      name: string;
      task: string;
      project: string;
    }): Promise<SuccessData> => {
      return executeApiCall({
        path: '/projects/tasks/name',

        payload,
      });
    },
    messages: {
      success: (t) => ({
        title: t('task-name-changed'),
        type: 'success',
      }),

      error: (t) => ({
        title: t('task-name-not-changed'),
        type: 'error',
      }),
    },
  },

  cancelTask: {
    action: async (payload: {
      task: string;
      project: string;
      justification: string;
    }): Promise<SuccessData> => {
      return executeApiCall({
        path: '/projects/tasks/cancel',

        payload,
      });
    },

    messages: {
      success: (t) => t('task-canceled-successfully')!,

      error: (t) => t('task-cancellation-failed')!,
    },
  },

  requestTaskDateChange: {
    action: async (payload: {
      justification: string;
      date: string;
      task: string;
      project: string;
    }): Promise<SuccessData> => {
      return executeApiCall({
        path: '/projects/tasks/date-change-request',

        payload,
      });
    },

    messages: {
      success: (t) => ({
        title: t('date-change-request-submitted'),
        type: 'success',
      }),

      error: (t) => ({
        title: t('could-not-request-date-change'),
        description: t('try-again-in-a-few-moments')!,
        type: 'error',
      }),
    },
  },

  answerTaskDateChange: {
    action: async (payload: {
      justification: string;
      date: string | null;
      approved: boolean;
      task: string;
      project: string;
    }): Promise<SuccessData> => {
      return executeApiCall({
        path: '/projects/tasks/date-change-response',

        payload,
      });
    },

    messages: {
      suppressSuccess: true,
      error: (t) => ({
        title: t('error-while-executing-date-change-request'),
        description: t('try-again-in-a-few-moments') as string,
      }),
    },
  },

  changeTaskOwnership: {
    action: async ({ owner, subOwner, ...params }: ChangeOwnershipParams): Promise<SuccessData> => {
      return executeApiCall({
        path: `/projects/tasks/${owner ? 'owner' : 'sub-owner'}`,
        method: 'put',
        payload: {
          ...params,
          owner: owner || subOwner,
        },
      });
    },

    messages: {
      success: (t) => ({
        title: t('task-ownership-changed'),
        type: 'error',
      }),

      error: (t) => ({
        title: t('task-ownership-error'),
        type: 'success',
      }),
    },
  },

  answerTaskOutsource: {
    action: async (payload: {
      approved: boolean;
      justification?: string;
      project: string;
      id: string;
    }): Promise<SuccessData> => {
      return executeApiCall({
        path: '/projects/outsource-response',

        payload,
      });
    },

    messages: {
      suppressSuccess: true,

      error: (t) => ({
        title: t('error-while-reviewing-outsource'),
      }),
    },
  },
});
